"use client";

import React, { useTransition } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { reviewProduct } from "@/services/products.service";
import toast from "react-hot-toast";
import { Separator } from "../../ui/separator";

const reviewSchema = z.object({
  rating: z.number().min(1, "Please select a rating"),
  comment: z.string().min(10, "Your review must be at least 10 characters"),
});

type ReviewFormValues = z.infer<typeof reviewSchema>;

export default function ProductReviewForm({
  productId,
}: {
  productId: number;
}) {
  const t = useTranslations();

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      rating: 0,
      comment: "",
    },
  });

  const [hoverRating, setHoverRating] = React.useState(0);
  const [isPending, startTransition] = useTransition();

  const onSubmit = (values: ReviewFormValues) => {
    startTransition(async () => {
      const response = await reviewProduct(productId, values);
      console.log("response", response);
      if (response.success) {
        toast.success(t("reviewAddedSuccessfully"));
        form.reset();
      } else {
        toast.error(response.message);
      }
    });
  };

  return (
    <div className=" mt-10">
      <h3 className="text-xl font-semibold mb-6">{t("writeAreview")}</h3>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Rating */}
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("rating")}</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        onClick={() => field.onChange(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className={cn(
                          "w-6 h-6 cursor-pointer transition-colors",
                          (hoverRating || field.value) >= star
                            ? "text-yellow-500 fill-yellow-500"
                            : "text-muted-foreground",
                        )}
                      />
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Comment */}
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("comment")}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={5}
                    placeholder={t("shareYourExperinceWithThisProduct")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" loading={isPending}>
            {t("submitReview")}
          </Button>
        </form>
      </Form>
      <Separator className="mt-10" />
    </div>
  );
}
